import Files from '$icons/FilesIcon.svelte';
import GalleryType from '$icons/GalleryTypeIcon.svelte';
import SeedFull from '$icons/SeedFullIcon.svelte';
import NumberOfItems from '$icons/NumberOfItemsIcon.svelte';
import lastModifiedIcon from '$icons/LastModifiedAltIcon.svelte';
import Contributor from '$icons/ContributorIcon.svelte';
import TypeOfFileFormat from '$icons/TypeOfFileFormatIcon.svelte';
import CalendarFull from '$icons/CalendarFullIcon.svelte';
import Hourglass from '$icons/HourglassIcon.svelte';
import CalendarDay from '$icons/CalendarDayIcon.svelte';
import type { ComponentProps, ComponentType } from 'svelte';
import FilterDetails_Adapter from '../../lib/components/homepage/filters/filter-details/FilterDetails_Adapter.svelte';
import FilterDetails_Files from '../../lib/components/homepage/filters/filter-details/FilterDetails_Files.svelte';
import FilterDetails_SeedsReceived from '../../lib/components/homepage/filters/filter-details/FilterDetails_SeedsReceived.svelte';
import FilterDetails_SeedsDonated from '../../lib/components/homepage/filters/filter-details/FilterDetails_SeedsDonated.svelte';
import FilterDetails_Contributors from '../../lib/components/homepage/filters/filter-details/FilterDetails_Contributors.svelte';
import FilterDetails_TypeOfFileFormat from '../../lib/components/homepage/filters/filter-details/FilterDetails_TypeOfFileFormat.svelte';
import FilterDetails_ProjectTimeline from '../../lib/components/homepage/filters/filter-details/FilterDetails_ProjectTimeline.svelte';
import FilterDetails_ProjectDuration from '../../lib/components/homepage/filters/filter-details/FilterDetails_ProjectDuration.svelte';
import FilterDetails_StartDate from '../../lib/components/homepage/filters/filter-details/FilterDetails_StartDate.svelte';
import FilterDetails_CompletionDate from '../../lib/components/homepage/filters/filter-details/FilterDetails_CompletionDate.svelte';
import FilterDetails_Location from '../../lib/components/homepage/filters/filter-details/FilterDetails_Location.svelte';
import FilterDetails_LastModified from '../../lib/components/homepage/filters/filter-details/FilterDetails_LastModified.svelte';
import FilterDetails_NumberOfItems from '../../lib/components/homepage/filters/filter-details/FilterDetails_NumberOfItems.svelte';
import FilterDetails_GalleryType from '../../lib/components/homepage/filters/filter-details/FilterDetails_GalleryType.svelte';
import type { galleryFiltersSchema, postFiltersSchema } from 'garden-common';
import type { feedSortOptions, peopleFiltersSchema, peopleSortOptions } from 'garden-common';
import type { z } from 'zod';
import type { SubcatInputConfig } from '$src/types/dataFieldWrapper';

export type PostFiltersSchema = z.infer<typeof postFiltersSchema>;
export type PeopleFiltersSchema = z.infer<typeof peopleFiltersSchema>;
export type GalleryFiltersSchema = z.infer<typeof galleryFiltersSchema>;

export type PostFiltersSchemaWithDynamic = PostFiltersSchema & {
  from?: string[];
  dynamicFilters: Record<string, string>;
};

export type PeopleFiltersSchemaWithDynamic = PeopleFiltersSchema & {
  from?: string[];
  dynamicFilters?: Record<string, string>;
};

export function getEmptyFiltersWithDynamic(): PostFiltersSchemaWithDynamic {
  return {
    dynamicFilters: {}
  };
}

interface FilterConfigBase {
  id: string;
  label: string;
  icon: ComponentType;
  configComponent: unknown;
  dynamicInputConfig?: unknown;
}

export interface FilterConfigStatic extends FilterConfigBase {
  id: string;
  configComponent: ComponentType;
}

export interface PeopleFilterConfigStatic extends FilterConfigBase {
  id: string;
  configComponent: ComponentType;
}

export interface FilterConfigDynamic extends FilterConfigBase {
  configComponent: ComponentType<FilterDetails_Adapter>;
  dynamicInputConfig: ComponentProps<FilterDetails_Adapter>['inputConfig'];
}

export type FilterConfig = FilterConfigStatic | FilterConfigDynamic | PeopleFilterConfigStatic;
export type FilterValidityRecord = Record<FilterConfig['label'], boolean>;

export function getDynamicFilterConfig(
  subcategoryName: string,
  sectionName: string,
  dynamicInputConfig: SubcatInputConfig
): FilterConfigDynamic {
  const conf: FilterConfigDynamic = {
    configComponent: FilterDetails_Adapter,
    dynamicInputConfig,
    icon: null,
    label: dynamicInputConfig.props.label as string,
    id: subcategoryName + '.' + sectionName + '.' + dynamicInputConfig.props.label
  };

  return conf;
}

export const additionalPostFilters: FilterConfigStatic[] = [
  {
    label: 'Files',
    id: 'files',
    icon: Files,
    configComponent: FilterDetails_Files
  },
  {
    label: 'Seeds received',
    id: 'seedsReceived',
    icon: SeedFull,
    configComponent: FilterDetails_SeedsReceived
  },
  {
    label: 'Contributors',
    id: 'collaborators',
    icon: Contributor,
    configComponent: FilterDetails_Contributors
  },
  {
    label: 'Type of file format',
    id: 'typeOfFileFormat',
    icon: TypeOfFileFormat,
    configComponent: FilterDetails_TypeOfFileFormat
  }
];

export const additionalPeopleFilters: PeopleFilterConfigStatic[] = [
  {
    label: 'Location',
    id: 'location',
    icon: Files,
    configComponent: FilterDetails_Location
  },
  {
    label: 'Seeds received',
    id: 'seedsReceived',
    icon: SeedFull,
    configComponent: FilterDetails_SeedsReceived
  },
  {
    label: 'Seeds Donated',
    id: 'seedsDonated',
    icon: Contributor,
    configComponent: FilterDetails_SeedsDonated
  }
];

export const additionalGalleryFilters: FilterConfigStatic[] = [
  {
    label: 'Gallery Type',
    id: 'galleryType',
    icon: GalleryType,
    configComponent: FilterDetails_GalleryType
  },
  {
    label: 'Last Modified',
    id: 'lastModified',
    icon: lastModifiedIcon,
    configComponent: FilterDetails_LastModified
  },
  {
    label: 'Number of items',
    id: 'itemCount',
    icon: NumberOfItems,
    configComponent: FilterDetails_NumberOfItems
  },
  {
    label: 'Number of Contributors',
    id: 'contributors',
    icon: Contributor,
    configComponent: FilterDetails_Contributors
  }
];

export const timePostFilters: FilterConfig[] = [
  {
    label: 'Project timeline',
    id: 'projectTimeline',
    icon: CalendarFull,
    configComponent: FilterDetails_ProjectTimeline
  },
  {
    label: 'Project duration',
    id: 'projectDuration',
    icon: Hourglass,
    configComponent: FilterDetails_ProjectDuration
  },
  {
    label: 'Start date',
    id: 'startDate',
    icon: CalendarDay,
    configComponent: FilterDetails_StartDate
  },
  {
    label: 'Completion date',
    id: 'completionDate',
    icon: CalendarDay,
    configComponent: FilterDetails_CompletionDate
  }
];

export type SortOption = (typeof feedSortOptions)[number] | (typeof peopleSortOptions)[number];
